import { supabase } from './supabase';

export type AnalyticsEventType = 'view' | 'export';
export type ExportFormat = 'pdf' | 'docx' | 'txt' | 'md' | 'json' | 'html' | 'xml' | 'rtf';

interface TrackEventParams {
  documentId: string;
  eventType: AnalyticsEventType;
  exportFormat?: ExportFormat;
}

export async function trackAnalyticsEvent({ documentId, eventType, exportFormat }: TrackEventParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.warn('Cannot track analytics: User not authenticated');
      return;
    }

    const { error } = await supabase
      .from('document_analytics')
      .insert({
        document_id: documentId,
        user_id: user.id,
        event_type: eventType,
        export_format: exportFormat,
      });

    if (error) {
      console.error('Error tracking analytics event:', error);
    }
  } catch (error) {
    console.error('Error tracking analytics event:', error);
  }
}

export async function getDocumentAnalytics(userId: string) {
  try {
    const { data: documents } = await supabase
      .from('documents')
      .select('id')
      .eq('user_id', userId);

    const documentIds = documents?.map(d => d.id) || [];

    if (documentIds.length === 0) {
      return {
        totalExports: 0,
        totalViews: 0,
        exportsByFormat: {},
      };
    }

    const { data: analytics, error } = await supabase
      .from('document_analytics')
      .select('event_type, export_format')
      .in('document_id', documentIds);

    if (error) throw error;

    const totalExports = analytics?.filter(a => a.event_type === 'export').length || 0;
    const totalViews = analytics?.filter(a => a.event_type === 'view').length || 0;

    const exportsByFormat: Record<string, number> = {};
    analytics?.filter(a => a.event_type === 'export' && a.export_format).forEach(a => {
      exportsByFormat[a.export_format!] = (exportsByFormat[a.export_format!] || 0) + 1;
    });

    return {
      totalExports,
      totalViews,
      exportsByFormat,
    };
  } catch (error) {
    console.error('Error getting document analytics:', error);
    return {
      totalExports: 0,
      totalViews: 0,
      exportsByFormat: {},
    };
  }
}
